import * as THREE from 'three';

type Shard = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  rotVel: THREE.Vector3;
  life: number;
  maxLife: number;
};

export class FpsShieldCrackVfx {
  private readonly shards: Shard[] = [];
  private readonly geo = new THREE.ConeGeometry(0.06, 0.16, 4);

  constructor(private readonly scene: THREE.Scene) {}

  /** intensity 0..1 scales shard count, speed, and opacity (damage chunk size). */
  spawn(pos: THREE.Vector3, intensity = 1): void {
    const power = Math.max(0.12, Math.min(1, intensity));
    const count = Math.max(4, Math.round(25 * power));
    const speedMul = 0.45 + power * 0.95;
    const lifeMul = 0.55 + power * 0.65;

    for (let i = 0; i < count; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0x5ef0ff,
        transparent: true,
        opacity: 0.35 + power * 0.6,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      const mesh = new THREE.Mesh(this.geo, mat);
      mesh.position.copy(pos);
      mesh.position.y += 0.5 + (Math.random() - 0.5) * 0.5;
      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
      mesh.scale.setScalar((0.55 + Math.random() * 0.55) * (0.7 + power * 0.55));
      this.scene.add(mesh);

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos((Math.random() - 0.5) * 2);
      const speed = (1.8 + Math.random() * 2.8) * speedMul;

      const vel = new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.sin(phi) * Math.sin(theta) * speed + 0.35 * power,
        Math.cos(phi) * speed,
      );

      const rotVel = new THREE.Vector3(
        (4 + Math.random() * 10) * power,
        (4 + Math.random() * 10) * power,
        (4 + Math.random() * 10) * power,
      );

      this.shards.push({
        mesh,
        vel,
        rotVel,
        life: 0,
        maxLife: (0.22 + Math.random() * 0.2) * lifeMul,
      });
    }
  }

  update(dt: number): void {
    for (let i = this.shards.length - 1; i >= 0; i--) {
      const s = this.shards[i]!;
      s.life += dt;

      s.vel.y -= dt * 3.5;
      s.mesh.position.addScaledVector(s.vel, dt);

      s.mesh.rotation.x += s.rotVel.x * dt;
      s.mesh.rotation.y += s.rotVel.y * dt;
      s.mesh.rotation.z += s.rotVel.z * dt;

      const ratio = s.life / s.maxLife;
      const mat = s.mesh.material as THREE.MeshBasicMaterial;

      mat.opacity = Math.max(0, mat.opacity * (1 - ratio * ratio));
      s.mesh.scale.setScalar(s.mesh.scale.x * (1 - ratio * 0.35));

      if (s.life >= s.maxLife) {
        this.scene.remove(s.mesh);
        mat.dispose();
        this.shards.splice(i, 1);
      }
    }
  }

  dispose(): void {
    for (const s of this.shards) {
      this.scene.remove(s.mesh);
      (s.mesh.material as THREE.Material).dispose();
    }
    this.shards.length = 0;
    this.geo.dispose();
  }
}
